export function buildCreatorAnchors(offsets = [], maxOffset = 0) {
  if (!offsets.length) return [0];

  const firstOffset = Number(offsets[0]) || 0;
  const limit = Math.max(0, Number(maxOffset) || 0);
  if (offsets.length === 1 || limit === 0) return [0];

  const normalizedOffsets = offsets.map((offset) => Math.max(0, (Number(offset) || 0) - firstOffset));
  const fullTrackSpan = normalizedOffsets.at(-1);
  if (!Number.isFinite(fullTrackSpan) || fullTrackSpan <= 0) return [0];

  return normalizedOffsets.map((offset, index) => {
    if (index === 0) return 0;
    if (index === normalizedOffsets.length - 1) return limit;
    return (offset / fullTrackSpan) * limit;
  });
}

export function resolveCreatorVideoIndex({
  isMobile = false,
  hoverCapable = true,
  designMode = false,
  currentIndex = 0,
  hoveredIndex = null,
  visible = false,
  documentHidden = false,
  reducedMotion = false,
  moving = false,
  scrolling = false,
  touching = false,
} = {}) {
  if (!visible || documentHidden || reducedMotion || moving || touching) return null;
  if (isMobile && designMode) return null;
  if (!isMobile && scrolling) return null;
  if (!isMobile && !hoverCapable) return null;
  const requestedIndex = isMobile ? currentIndex : hoveredIndex;
  return Number.isInteger(requestedIndex) && requestedIndex >= 0 ? requestedIndex : null;
}

export class CreatorVideoPlaybackController {
  constructor({ videos = [] } = {}) {
    this.videos = videos;
    this.activeIndex = null;
    this.requestToken = 0;
    this.activeCleanup = null;
  }

  play(index) {
    const requestedIndex = Number(index);
    const video = Number.isInteger(requestedIndex) ? this.videos[requestedIndex] : null;
    const source = video?.dataset?.videoSrc;
    if (!video || !source) {
      this.stop();
      return false;
    }
    if (this.activeIndex === requestedIndex && video.getAttribute?.('src') === source) {
      const token = this.requestToken;
      try {
        video.play?.()?.catch?.(() => {
          if (this.activeIndex === requestedIndex && this.requestToken === token) this.stop();
        });
      } catch {
        if (this.activeIndex === requestedIndex && this.requestToken === token) this.stop();
        return false;
      }
      return true;
    }

    this.stop();
    const token = ++this.requestToken;
    this.activeIndex = requestedIndex;
    let cleanup = () => {};
    const reveal = () => {
      if (this.activeIndex !== requestedIndex || this.requestToken !== token) return;
      if (video.getAttribute?.('src') !== source) return;
      cleanup();
      video.dataset.playing = 'true';
    };
    const fail = () => {
      if (this.activeIndex === requestedIndex && this.requestToken === token) this.stop();
    };
    cleanup = () => {
      video.removeEventListener?.('playing', reveal);
      video.removeEventListener?.('error', fail);
      if (this.activeCleanup === cleanup) this.activeCleanup = null;
    };
    this.activeCleanup = cleanup;
    video.addEventListener?.('playing', reveal, { once: true });
    video.addEventListener?.('error', fail, { once: true });
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'none';
    video.setAttribute('src', source);
    video.load?.();
    try {
      const playback = video.play?.();
      playback?.catch?.(fail);
    } catch {
      fail();
      return false;
    }
    if (!video.addEventListener) reveal();
    return true;
  }

  stop() {
    this.requestToken += 1;
    this.activeCleanup?.();
    this.activeCleanup = null;
    if (this.activeIndex === null) return;
    const video = this.videos[this.activeIndex];
    video?.pause?.();
    video?.removeAttribute?.('src');
    if (video?.dataset) delete video.dataset.playing;
    video?.load?.();
    this.activeIndex = null;
  }

  destroy() {
    this.stop();
    this.videos = [];
  }
}

export class CreatorAutoplayController {
  constructor({
    intervalMs = 3000,
    onTick,
    setIntervalFn = globalThis.setInterval.bind(globalThis),
    clearIntervalFn = globalThis.clearInterval.bind(globalThis),
  }) {
    this.intervalMs = Math.max(3000, Number(intervalMs) || 3000);
    this.onTick = onTick;
    this.setIntervalFn = setIntervalFn;
    this.clearIntervalFn = clearIntervalFn;
    this.timer = null;
    this.state = {
      enabled: false,
      count: 0,
      userPaused: false,
      hidden: false,
      offscreen: false,
      reducedMotion: false,
      hovered: false,
      focused: false,
      touching: false,
    };
  }

  update(patch) {
    Object.assign(this.state, patch);
    this.sync();
  }

  pause() {
    this.state.userPaused = true;
    this.sync();
  }

  play() {
    this.state.userPaused = false;
    this.sync();
  }

  setIntervalMs(intervalMs) {
    const nextInterval = Math.max(3000, Number(intervalMs) || 3000);
    if (nextInterval === this.intervalMs) return;
    this.intervalMs = nextInterval;
    if (this.timer !== null) this.clearIntervalFn(this.timer);
    this.timer = null;
    this.sync();
  }

  canRun() {
    const state = this.state;
    return state.enabled && state.count > 1 && !state.userPaused && !state.hidden && !state.offscreen
      && !state.reducedMotion && !state.hovered && !state.focused && !state.touching;
  }

  sync() {
    if (!this.canRun()) {
      if (this.timer !== null) this.clearIntervalFn(this.timer);
      this.timer = null;
      return;
    }
    if (this.timer !== null) return;
    this.timer = this.setIntervalFn(this.onTick, this.intervalMs);
  }

  destroy() {
    if (this.timer !== null) this.clearIntervalFn(this.timer);
    this.timer = null;
  }
}

export class LSCreatorGalleryCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.viewport = this.querySelector('[data-ls-creator-viewport]');
    this.track = this.querySelector('[data-ls-creator-track]');
    this.slides = [...this.querySelectorAll('[data-ls-creator-item]')];
    this.videos = this.slides.map((slide) => slide.querySelector('[data-ls-creator-video]'));
    this.dots = [...this.querySelectorAll('[data-ls-creator-dot]')];
    this.previousButton = this.querySelector('[data-ls-creator-previous]');
    this.nextButton = this.querySelector('[data-ls-creator-next]');
    this.autoplayButton = this.querySelector('[data-ls-creator-autoplay-toggle]');
    this.autoplayIcon = this.querySelector('[data-ls-creator-autoplay-icon]');
    this.liveRegion = this.querySelector('[data-ls-creator-live]');
    this.currentIndex = Number(this.dataset.currentIndex) || 0;
    this.anchors = [0];
    this.touchStart = null;
    this.suppressNextClick = false;
    this.clickSuppressionTimeout = null;
    this.trackMotionTimeout = null;
    this.pageScrollTimeout = null;
    this.hoveredIndex = null;
    this.isTrackMoving = false;
    this.isPageScrolling = false;
    this.isVisible = false;
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.mobileQuery = globalThis.matchMedia?.('(max-width: 989px)');
    this.isMobile = this.mobileQuery?.matches || false;
    this.hoverQuery = globalThis.matchMedia?.('(hover: hover) and (pointer: fine)');
    this.hoverCapable = this.hoverQuery?.matches ?? true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.videoController = new CreatorVideoPlaybackController({ videos: this.videos });
    this.controller = new CreatorAutoplayController({
      intervalMs: this.isMobile ? this.dataset.mobileAutoplayInterval : this.dataset.autoplayInterval,
      onTick: () => this.next({ announce: false }),
    });

    this.addEventListener('mouseenter', () => {
      if (!this.isMobile && this.hoverCapable) this.updateController({ hovered: true });
    }, { signal });
    this.addEventListener('mouseleave', () => {
      this.hoveredIndex = null;
      if (!this.isMobile) this.updateController({ hovered: false });
      else this.syncVideoPlayback();
    }, { signal });
    this.slides.forEach((slide, index) => {
      slide.addEventListener('mouseenter', () => this.onItemMouseEnter(index), { signal });
      slide.addEventListener('mouseleave', () => this.onItemMouseLeave(index), { signal });
      slide.addEventListener('pointermove', (event) => this.onItemPointerMove(event, index), { signal });
    });
    this.addEventListener('focusin', () => this.updateController({ focused: true }), { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => this.updateController({ focused: this.matches(':focus-within') }));
    }, { signal });
    this.addEventListener('click', (event) => this.onClickCapture(event), { capture: true, signal });
    this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    this.viewport?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.viewport?.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.viewport?.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal });
    this.viewport?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.viewport?.addEventListener('wheel', () => this.onPageScroll(), { passive: true, signal });
    this.track?.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.finishTrackMotion();
    }, { signal });
    globalThis.addEventListener?.('scroll', () => this.onPageScroll(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => {
      this.updateController({ hidden: document.hidden });
    }, { signal });
    this.motionQuery?.addEventListener('change', (event) => {
      this.updateController({ reducedMotion: event.matches });
    }, { signal });
    this.mobileQuery?.addEventListener('change', (event) => this.onViewportModeChange(event.matches), { signal });
    this.hoverQuery?.addEventListener('change', (event) => {
      this.hoverCapable = event.matches;
      this.hoveredIndex = null;
      this.syncVideoPlayback();
    }, { signal });

    this.previousButton?.addEventListener('click', () => this.previous(), { signal });
    this.nextButton?.addEventListener('click', () => this.next(), { signal });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index), { signal });
    });
    this.autoplayButton?.addEventListener('click', () => {
      if (this.controller.state.userPaused) this.play();
      else this.pause();
    }, { signal });

    this.resizeObserver = globalThis.ResizeObserver ? new ResizeObserver(() => {
      this.render({ animate: false, recalculate: true });
    }) : null;
    this.resizeObserver?.observe(this.viewport);
    this.intersectionObserver = globalThis.IntersectionObserver ? new IntersectionObserver(([entry]) => {
      this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      this.updateController({ offscreen: !this.isVisible });
    }, { rootMargin: '0px', threshold: 0.15 }) : null;
    this.intersectionObserver?.observe(this);

    this.setAttribute('data-ready', '');
    this.render({ animate: false, recalculate: true });
    this.updateController({
      enabled: this.dataset.autoplay !== 'false' && this.dataset.designMode !== 'true',
      count: this.anchors.length,
      hidden: document.hidden,
      offscreen: !this.isVisible,
      reducedMotion: this.motionQuery?.matches || false,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.controller?.destroy();
    this.videoController?.destroy();
    if (this.clickSuppressionTimeout !== null) globalThis.clearTimeout(this.clickSuppressionTimeout);
    if (this.trackMotionTimeout !== null) globalThis.clearTimeout(this.trackMotionTimeout);
    if (this.pageScrollTimeout !== null) globalThis.clearTimeout(this.pageScrollTimeout);
    this.controller = null;
    this.videoController = null;
  }

  onViewportModeChange(isMobile) {
    this.isMobile = Boolean(isMobile);
    this.hoveredIndex = null;
    this.videoController?.stop();
    this.controller?.setIntervalMs(
      this.isMobile ? this.dataset.mobileAutoplayInterval : this.dataset.autoplayInterval,
    );
    this.updateController({
      enabled: this.dataset.autoplay !== 'false' && this.dataset.designMode !== 'true',
      hovered: false,
    });
  }

  onItemMouseEnter(index) {
    if (this.isMobile || !this.hoverCapable || this.isPageScrolling || this.isTrackMoving) return;
    this.hoveredIndex = index;
    this.syncVideoPlayback();
  }

  onItemMouseLeave(index) {
    if (this.hoveredIndex !== index) return;
    this.hoveredIndex = null;
    this.syncVideoPlayback();
  }

  onItemPointerMove(event, index) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (this.isMobile || !this.hoverCapable || this.isPageScrolling || this.isTrackMoving) return;
    if (this.hoveredIndex === index) return;
    this.hoveredIndex = index;
    this.syncVideoPlayback();
  }

  onPageScroll() {
    if (this.isMobile) return;
    this.isPageScrolling = true;
    this.hoveredIndex = null;
    this.videoController?.stop();
    if (this.pageScrollTimeout !== null) globalThis.clearTimeout(this.pageScrollTimeout);
    this.pageScrollTimeout = globalThis.setTimeout(() => {
      this.pageScrollTimeout = null;
      this.isPageScrolling = false;
      this.hoveredIndex = null;
      this.videoController?.stop();
    }, 180);
  }

  onBlockSelect(event) {
    if (this.dataset.designMode !== 'true') return;
    const slide = event.target?.closest?.('[data-ls-creator-item]');
    const index = this.slides.indexOf(slide);
    if (index < 0) return;
    this.hoveredIndex = null;
    this.videoController?.stop();
    this.goTo(index, { announce: false, animate: false });
  }

  beginTrackMotion() {
    this.isTrackMoving = true;
    if (!this.isMobile) this.hoveredIndex = null;
    this.videoController?.stop();
    if (this.trackMotionTimeout !== null) globalThis.clearTimeout(this.trackMotionTimeout);
    this.trackMotionTimeout = globalThis.setTimeout(() => this.finishTrackMotion(), 620);
  }

  finishTrackMotion() {
    if (this.trackMotionTimeout !== null) globalThis.clearTimeout(this.trackMotionTimeout);
    this.trackMotionTimeout = null;
    this.isTrackMoving = false;
    this.syncVideoPlayback();
  }

  syncVideoPlayback() {
    if (!this.videoController) return;
    const targetIndex = resolveCreatorVideoIndex({
      isMobile: this.isMobile,
      hoverCapable: this.hoverCapable,
      designMode: this.dataset.designMode === 'true',
      currentIndex: this.currentIndex,
      hoveredIndex: this.hoveredIndex,
      visible: this.isVisible,
      documentHidden: globalThis.document?.hidden || false,
      reducedMotion: this.motionQuery?.matches || false,
      moving: this.isTrackMoving,
      scrolling: this.isPageScrolling,
      touching: this.controller?.state.touching || false,
    });
    if (targetIndex === null) this.videoController.stop();
    else this.videoController.play(targetIndex);
  }

  measureAnchors() {
    if (!this.viewport || !this.track || !this.slides.length) {
      this.anchors = [0];
      this.currentIndex = 0;
      return;
    }

    const offsets = this.slides.map((slide) => slide.offsetLeft);
    const endPadding = Number.parseFloat(globalThis.getComputedStyle?.(this.track)?.paddingRight) || 0;
    const maxOffset = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth + endPadding);
    this.anchors = buildCreatorAnchors(offsets, maxOffset);
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.anchors.length - 1));
  }

  render({ animate = true, recalculate = false } = {}) {
    if (!this.track) return;
    if (recalculate) this.measureAnchors();
    const offset = this.anchors[this.currentIndex] || 0;
    if (animate) {
      this.beginTrackMotion();
    } else {
      if (this.trackMotionTimeout !== null) globalThis.clearTimeout(this.trackMotionTimeout);
      this.trackMotionTimeout = null;
      this.isTrackMoving = false;
      this.videoController?.stop();
    }
    if (!animate) this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    if (!animate) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
    this.dataset.currentIndex = String(this.currentIndex);
    this.dataset.static = String(this.anchors.length <= 1);
    this.syncControls();
    if (this.controller && this.controller.state.count !== this.anchors.length) {
      this.controller.update({ count: this.anchors.length });
    }
    this.syncVideoPlayback();
  }

  syncControls() {
    this.dots.forEach((dot, index) => {
      dot.hidden = false;
      dot.setAttribute('aria-current', String(index === this.currentIndex));
    });
  }

  goTo(index, { announce = true, animate = true } = {}) {
    const count = this.anchors.length;
    if (count <= 1) {
      this.currentIndex = 0;
      this.render({ animate: false });
      return;
    }

    const requestedIndex = Number(index) || 0;
    const wrappedIndex = ((requestedIndex % count) + count) % count;
    const wrapped = requestedIndex !== wrappedIndex;
    this.currentIndex = wrappedIndex;
    this.render({ animate: wrapped ? false : animate });
    if (announce && this.liveRegion) {
      this.liveRegion.textContent = `Creator image ${this.currentIndex + 1} of ${count}`;
    }
  }

  next(options = {}) {
    if (this.anchors.length <= 1) return;
    this.goTo(this.currentIndex + 1, options);
  }

  previous(options = {}) {
    if (this.anchors.length <= 1) return;
    this.goTo(this.currentIndex - 1, options);
  }

  play() {
    if (this.controller?.state.reducedMotion) {
      this.syncUiState();
      return;
    }
    this.controller?.play();
    this.syncUiState();
  }

  pause() {
    this.controller?.pause();
    this.syncUiState();
  }

  updateController(patch) {
    this.controller?.update(patch);
    this.syncUiState();
    this.syncVideoPlayback();
  }

  syncUiState() {
    if (!this.controller) return;
    const { userPaused, reducedMotion } = this.controller.state;
    this.toggleAttribute('data-reduced-motion', reducedMotion);
    this.toggleAttribute('data-user-paused', userPaused);

    if (this.anchors.length <= 1) {
      this.dataset.autoplayState = 'static';
      return;
    }

    this.dataset.autoplayState = this.controller.canRun() ? 'playing' : 'paused';
    if (!this.autoplayButton) return;

    if (reducedMotion) {
      this.autoplayButton.disabled = true;
      this.autoplayButton.setAttribute('aria-pressed', 'false');
      this.autoplayButton.setAttribute('aria-label', 'Automatic creator gallery rotation is off because reduced motion is enabled');
      if (this.autoplayIcon) this.autoplayIcon.textContent = '—';
      return;
    }

    this.autoplayButton.disabled = false;
    this.autoplayButton.setAttribute('aria-pressed', String(!userPaused));
    this.autoplayButton.setAttribute('aria-label', userPaused ? 'Play automatic creator gallery rotation' : 'Pause automatic creator gallery rotation');
    if (this.autoplayIcon) this.autoplayIcon.textContent = userPaused ? '▶' : 'Ⅱ';
  }

  onPointerDown(event) {
    if (event.pointerType !== 'touch') return;
    this.touchStart = { x: event.clientX, y: event.clientY };
    this.viewport?.setPointerCapture?.(event.pointerId);
    this.updateController({ touching: true });
  }

  onPointerUp(event) {
    if (event.pointerType !== 'touch' || !this.touchStart) return;
    const distanceX = event.clientX - this.touchStart.x;
    const distanceY = event.clientY - this.touchStart.y;
    const isHorizontalSwipe = Math.abs(distanceX) >= 40 && Math.abs(distanceX) > Math.abs(distanceY);
    this.touchStart = null;
    this.viewport?.releasePointerCapture?.(event.pointerId);
    if (!isHorizontalSwipe) {
      this.updateController({ touching: false });
      return;
    }
    this.suppressFollowingClick();
    if (distanceX < 0) this.next();
    else this.previous();
    this.updateController({ touching: false });
  }

  onPointerCancel(event) {
    if (event.pointerType !== 'touch') return;
    this.touchStart = null;
    this.viewport?.releasePointerCapture?.(event.pointerId);
    this.updateController({ touching: false });
  }

  suppressFollowingClick() {
    this.suppressNextClick = true;
    if (this.clickSuppressionTimeout !== null) globalThis.clearTimeout(this.clickSuppressionTimeout);
    this.clickSuppressionTimeout = globalThis.setTimeout(() => {
      this.suppressNextClick = false;
      this.clickSuppressionTimeout = null;
    }, 450);
  }

  onClickCapture(event) {
    if (!this.suppressNextClick) return;
    this.suppressNextClick = false;
    if (this.clickSuppressionTimeout !== null) globalThis.clearTimeout(this.clickSuppressionTimeout);
    this.clickSuppressionTimeout = null;
    event.preventDefault();
    event.stopPropagation();
  }

  onKeydown(event) {
    if (event.target !== this.viewport || this.anchors.length <= 1) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.anchors.length - 1);
    }
  }
}

if (!customElements.get('ls-creator-gallery-carousel')) {
  customElements.define('ls-creator-gallery-carousel', LSCreatorGalleryCarousel);
}
